import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box
} from '@mui/material';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

import { useNavigate, Link } from 'react-router-dom';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import CommonRestricted from '../../common/CommonRestricted';

import { icons } from '../../common/CommonIcons';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useGroups, groupTypes, targetTypes } from '../../../hooks/api/useGroups';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

export default function ListAdministrators({
    routeType
}){
    const { t } = useTranslations({ ns: ["common", "@group"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list: get, remove } = useGroups({ groupType: groupTypes.administrator });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.group.id, scopeTypes.group.administrator.id)

    const [ selectedIds, setSelectedIds ] = useState([])
    const [ refreshDataGrid, setRefreshDataGrid ] = useState(0)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const gridRef = useRef();

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try{

            props.dataTypes = ["owner_count"];

            const result = await get(props)

            return {
                data: result?.data?.groups,
                count: result?.data?.total
            }

        }catch(error){

            promptMessage(false, `Error getting results (${error?.message})`)
            
            return {
                data: [],
                count: 0
            }
        }  
    }

    useEffect (()=>{ 
        defineColumnsView()
    },[])
    
    const defineColumnsView = () =>{
        defineColumns ([
            { 
                name: 'label', 
                header: t('name'), 
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`groups/administrator/update/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            { 
                name: 'description', header: t('description') 
            },
            { 
                name: 'action_type', header: t('actionType' )
            },
            { 
                name: 'owner_count', 
                header: t('owners'), 
                renderValue: ({ value, cellProps }) => {

                    let displayValue = parseInt(value, 10) > 0? `${value} Users` : ``

                    return (
                        <>{displayValue}</>
                    )
                        
                },
            },
            { 
                name: 'status', 
                header: t('status'), 
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, ["Enabled", "Disabled"], ["primary", "default"], "")
            }
        ])
    }
    
    const handleDelete = async () => {

        try{
            let result = await remove({ ids: selectedIds, targetType: targetTypes.administrator })

            promptMessage(true, result?.message)
            setRefreshDataGrid(currentVal => currentVal + 1)

        }catch(error){
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("groups/administrator/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn: 
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}
                
            />
        )
    }

    return (
        <Box>
            {  
                getHtmlHead({ routeType, title: headTitles.administratorGroups })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("groupAdministrators")}
                breadcrumbs={[
                    {
                        displayText: t("groupAdministrators"),
                        props: {
                            // underline: "hover",
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}